import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tabs } from "@design-system/Tabs/Tabs";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ExpandedState } from "@tanstack/react-table";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { HStack } from "@design-system/HStack/HStack";
import { useEntities } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { BankReconciliation } from "./BankReconciliation";
import styles from "./ReconciliationPage.module.scss";

export const ReconciliationPage: React.FC = () => {
    const { personalAccountReconciliation: isPersonalReconciliationEnabled } =
        useTypedFlags();
    const entities = useEntities({ excludeMockEntities: true });
    const availableEntities = useMemo(
        () =>
            entities.filter((entity) =>
                entity.isPersonal
                    ? isPersonalReconciliationEnabled
                    : !!entity.legalType,
            ),
        [entities, isPersonalReconciliationEnabled],
    );
    const availableEntityIds = useMemo(
        () => availableEntities.map(({ id }) => id),
        [availableEntities],
    );

    const [cachedEntityIds, setCachedEntityIds] = useWorkspaceUserCache<
        number[] | undefined
    >(WORKSPACE_USER_CACHE_KEYS.RECONCILIATION_ENTITIES_CACHE_KEY);

    const [selectedEntityIds, setSelectedEntityIds] = useState<
        number[] | undefined
    >(undefined);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);

    useEffect(() => {
        if (selectedEntityIds === undefined && availableEntityIds.length > 0) {
            const fromCache = cachedEntityIds?.filter((id) =>
                availableEntityIds.includes(id),
            );
            setSelectedEntityIds(
                fromCache && fromCache.length > 0
                    ? fromCache
                    : availableEntityIds,
            );
        }
    }, [availableEntityIds, cachedEntityIds, selectedEntityIds]);

    const selectionKey = selectedEntityIds?.join(",");
    useEffect(() => {
        setExpanded({});
    }, [selectionKey]);

    const handleEntitiesChange = (entityIds: number[]) => {
        setSelectedEntityIds(entityIds);
        setCachedEntityIds(entityIds);
    };

    if (!selectedEntityIds) {
        return null;
    }

    return (
        <div className={styles.reconciliationPage}>
            <div className={styles.controls}>
                <Tabs.Root defaultValue="bank">
                    <Tabs.List className={styles.tabsList}>
                        <Tabs.Tab value="bank">Bank</Tabs.Tab>
                        <Tooltip content="Coming soon">
                            <Tabs.Tab value="loan" disabled>
                                Loan
                            </Tabs.Tab>
                        </Tooltip>
                    </Tabs.List>
                </Tabs.Root>
                <HStack gap={12}>
                    <CollapseAllButton
                        expanded={expanded}
                        tableRef={tableRef}
                    />
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onChange={(filters) =>
                            handleEntitiesChange(filters.entityIds ?? [])
                        }
                        onlyBusiness={!isPersonalReconciliationEnabled}
                        mode="selection"
                    />
                </HStack>
            </div>

            <BankReconciliation
                ref={tableRef}
                entityIds={selectedEntityIds}
                expanded={expanded}
                onExpandedChange={setExpanded}
            />
        </div>
    );
};
